import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  ChangelogFormDto,
  type ChangelogFormInterface
} from '@zaoblako/shared/modules/system/dtos/changelog/changelog-form.dto';
import type { ChangelogDescriptionBlockInterface } from '@zaoblako/shared/modules/system/dtos/changelog/changelog-description-block.dto';
import { ChangelogService } from '@zaoblako/web/core/services/basis/changelog.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { CheckboxModule } from 'primeng/checkbox';
import { CardModule } from 'primeng/card';
import { Observable } from 'rxjs';
import { APPLICATION_MODULES } from '@zaoblako/shared/modules/security/constants/application-access-control.constants';

@Component({
  selector: 'crm-changelog-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    EditorComponent,
    DatepickerComponent,
    ButtonComponent,
    SpinnerComponent,
    MultiSelectComponent,
    CheckboxModule
  ],
  templateUrl: './changelog-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangelogFormComponent extends BaseFormComponent<ChangelogFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public changelog = <ChangelogFormInterface>{};
  public dto = ChangelogFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;
  public modules = Object.keys(APPLICATION_MODULES).filter((key) => key !== 'BASIS');

  public constructor(
    private readonly changelogService: ChangelogService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.changelogService.findManyWithPagination({ limit: 10, offset: 0 }).subscribe((changelogs) => {
        if (changelogs?.items?.length) {
          const version = changelogs.items.sort()[0]?.version;
          const lastNumber = +version?.split('.')[2] + 1;
          this.changelog.version = `${version?.split('.')[0]}.${version?.split('.')[1]}.${lastNumber}`;
          this.changelog = { ...this.changelog };
          this.ensureBlocks();
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.changelogService
      .findOneByVersion(this.formStoreService.getState.forms[this.index]?.entityId as any)
      .subscribe({
        next: (changelog) => {
          this.changelog = changelog;
          this.ensureBlocks();
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public getModuleDisplayName = (key: string) => APPLICATION_MODULES[key]?.displayName || key;
  public getModuleDescription = (key: string) => APPLICATION_MODULES[key]?.description || '';

  public addBlock() {
    const blocks = (this.changelog.descriptionBlocks || []) as ChangelogDescriptionBlockInterface[];
    blocks.push({
      sortOrder: blocks.length,
      description: '',
      forAllTariffs: true,
      modules: []
    });
    this.changelog.descriptionBlocks = blocks;
    this.cdRef.markForCheck();
  }

  public removeBlock(index: number) {
    const blocks = ((this.changelog.descriptionBlocks || []) as ChangelogDescriptionBlockInterface[])
      .filter((_, i) => i !== index)
      .map((b, idx) => ({ ...b, sortOrder: idx }));
    this.changelog.descriptionBlocks = blocks;
    this.cdRef.markForCheck();
  }

  public onToggleForAllTariffs(block: ChangelogDescriptionBlockInterface) {
    if (block.forAllTariffs) {
      block.modules = [];
    }
  }

  public saveChangelog() {
    // Normalize order before send
    if (this.changelog.descriptionBlocks?.length) {
      this.changelog.descriptionBlocks = (this.changelog.descriptionBlocks as any[])
        .map((b, idx) => ({ ...b, sortOrder: idx }));
    }

    if (this.checkValidate(this.changelog).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.changelogService
          .createOrUpdateOne(this.formStoreService.getState.forms[this.index]?.entityId as any, this.changelog)
          .subscribe({
            next: (changelog) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Версия "${this.changelog.version}" изменена`);
              this.formStoreService.saveNext(changelog, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.changelogService.createOrUpdateOne(this.changelog.version, this.changelog).subscribe({
          next: (changelog) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Версия создана');
            this.formStoreService.saveNext(changelog, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteChangelog() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить версию "${this.changelog.version}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.changelogService
          .deleteOneByVersion(this.formStoreService.getState.forms[this.index]?.entityId as any)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Версия "${this.changelog.version}" удалена`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  private ensureBlocks() {
    if (this.changelog.descriptionBlocks?.length) {
      // Ensure stable order
      this.changelog.descriptionBlocks = (this.changelog.descriptionBlocks as any[])
        .slice()
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((b, idx) => ({ ...b, sortOrder: idx }));
      return;
    }
    // Legacy fallback: create a single block from description
    const legacy = this.changelog.description || '';
    this.changelog.descriptionBlocks = [{
      sortOrder: 0,
      description: legacy,
      forAllTariffs: true,
      modules: []
    }];
  }
}
